import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, Share2, ChevronRight, BookOpen, Sparkles, CheckCircle2 } from 'lucide-react';
import { blogAPI } from '../lib/api';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

const CATEGORY_COLORS = {
  guide: 'bg-blue-100 text-blue-700',
  comparatif: 'bg-purple-100 text-purple-700',
  test: 'bg-amber-100 text-amber-700',
  actu: 'bg-green-100 text-green-700',
};


const CATEGORY_FALLBACKS = {
  guide: '/assets/padsmash_logo_main.png',
  comparatif: '/assets/padsmash_logo_main.png',
  test: '/assets/padsmash_logo_main.png',
  actu: '/assets/padsmash_logo_main.png',
};

const getArticleImage = (article) => {
  if (article && article.image) {
    if (article.image.startsWith('http') || article.image.startsWith('/api/') || article.image.startsWith('/assets/')) {
      return article.image;
    }
  }
  return CATEGORY_FALLBACKS[article && article.category] || CATEGORY_FALLBACKS.guide;
};

// Markdown renderers (defined outside component to avoid re-creating per render)
const MD_H2 = ({ children }) => {
  const text = String(children);
  const id = slugifyHeading(text);
  return (
    <h2 id={id} className="group relative">
      <span className="absolute -left-4 sm:-left-6 top-1 inline-block w-1.5 h-8 bg-gradient-to-b from-[#0C63BF] to-[#32CD32] rounded-full" />
      {children}
    </h2>
  );
};

const MD_H3 = ({ children }) => (
  <h3 className="flex items-start gap-2">
    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#32CD32] mt-3 shrink-0" />
    <span>{children}</span>
  </h3>
);

const MD_A = ({ node, children, href, ...props }) => {
  if (href && href.startsWith('/')) return <Link to={href} {...props}>{children}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
};

const MD_UL = ({ children }) => (
  <ul className="list-none pl-0 space-y-3">{children}</ul>
);

const MD_LI = ({ children }) => (
  <li className="flex items-start gap-3 text-[#3D5870] leading-relaxed text-[17px]">
    <CheckCircle2 className="w-5 h-5 text-[#32CD32] mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);

// Table components — rendered when GitHub-flavored markdown tables are present
const MD_TABLE = ({ children }) => (
  <div className="my-8 overflow-x-auto rounded-2xl border border-[#E8F4FD] shadow-sm">
    <table className="min-w-full text-sm border-collapse">{children}</table>
  </div>
);
const MD_THEAD = ({ children }) => (
  <thead className="bg-gradient-to-r from-[#033871] to-[#0C63BF] text-white">{children}</thead>
);
const MD_TBODY = ({ children }) => <tbody>{children}</tbody>;
const MD_TR = ({ children }) => (
  <tr className="even:bg-[#F7FAFB] odd:bg-white border-b border-[#F0F7FA] last:border-0">{children}</tr>
);
const MD_TH = ({ children }) => (
  <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-wider whitespace-nowrap">{children}</th>
);
const MD_TD = ({ children }) => (
  <td className="px-4 py-3 text-[#3D5870] align-top leading-relaxed">{children}</td>
);

const MARKDOWN_COMPONENTS = {
  h2: MD_H2,
  h3: MD_H3,
  a: MD_A,
  ul: MD_UL,
  li: MD_LI,
  table: MD_TABLE,
  thead: MD_THEAD,
  tbody: MD_TBODY,
  tr: MD_TR,
  th: MD_TH,
  td: MD_TD,
};

// Convert a heading text to a deterministic anchor id
const slugifyHeading = (text) => {
  return String(text)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
};

// Extract H2 list from markdown content (for ToC)
const extractTOC = (md) => {
  if (!md) return [];
  const lines = md.split('\n');
  const toc = [];
  for (const l of lines) {
    const m = l.match(/^##\s+(.+?)\s*$/);
    if (m) toc.push({ text: m[1].replace(/\*\*/g, '').trim(), id: slugifyHeading(m[1]) });
  }
  return toc;
};

export default function BlogArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').slice(0, 2);
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const [articleRes, relatedRes] = await Promise.all([
          blogAPI.getArticle(slug, lang),
          blogAPI.getArticles(null, lang),
        ]);
        setArticle(articleRes.data);
        setRelatedArticles(relatedRes.data.filter(a => a.slug !== slug).slice(0, 3));
      } catch (err) {
        console.error('Error fetching article:', err);
      }
      setLoading(false);
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug, lang]);

  // Reading progress bar
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop;
      const height = h.scrollHeight - h.clientHeight;
      setProgress(height > 0 ? (scrolled / height) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section tracker
  useEffect(() => {
    if (!article) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, { rootMargin: '-30% 0px -55% 0px' });
    document.querySelectorAll('article h2[id]').forEach(h => obs.observe(h));
    return () => obs.disconnect();
  }, [article]);

  const toc = useMemo(() => extractTOC(article && article.content), [article]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: article.excerpt, url: window.location.href });
      } catch (err) { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t('blog.link_copied'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F7FA] py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#E8F4FD] rounded w-3/4" />
            <div className="h-72 bg-[#E8F4FD] rounded-3xl" />
            <div className="space-y-3">
              <div className="h-4 bg-[#E8F4FD] rounded" />
              <div className="h-4 bg-[#E8F4FD] rounded" />
              <div className="h-4 bg-[#E8F4FD] rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F0F7FA] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-[#B8D4E3] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#033871] mb-2">{t('blog.not_found')}</h1>
          <Link to="/blog" className="text-[#0C63BF] font-medium hover:underline">{t('blog.back')}</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.meta_description,
    "image": (article.image && (article.image.startsWith('http') ? article.image : `https://www.padsmash.com${article.image}`)) || undefined,
    "author": { "@type": "Organization", "name": "PadSmash" },
    "publisher": {
      "@type": "Organization", "name": "PadSmash",
      "logo": { "@type": "ImageObject", "url": "https://www.padsmash.com/assets/padsmash_icon_bold.png" }
    },
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.padsmash.com/blog/${article.slug}` }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7FAFB] to-[#F0F7FA]">
      <SEO title={article.title} description={article.meta_description} path={`/blog/${article.slug}`} type="article" jsonLd={jsonLd} image={article.image} />

      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-transparent" data-testid="reading-progress-bar">
        <div className="h-full bg-gradient-to-r from-[#0C63BF] to-[#32CD32] transition-all duration-150" style={{ width: progress + '%' }} />
      </div>

      {/* Back link */}
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#5A8BA8] hover:text-[#033871] text-sm font-medium" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> {t('blog.back')}
        </Link>
      </div>

      {/* HERO — Image full-bleed with floating meta card */}
      <header className="max-w-5xl mx-auto px-4 pt-6 pb-2">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className={'px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ' + (CATEGORY_COLORS[article.category] || CATEGORY_COLORS.guide)}>
            {t(`blog.cat_${article.category}`, 'Article')}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-[#5A8BA8]">
            <Clock className="w-3 h-3" /> {article.read_time} min
          </span>
          {article.views > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-[#5A8BA8]">
              <Eye className="w-3 h-3" /> {t('blog.views', { n: article.views })}
            </span>
          )}
          <button onClick={handleShare} className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#0C63BF] hover:text-[#033871] font-medium" data-testid="share-btn">
            <Share2 className="w-3.5 h-3.5" /> {t('blog.share')}
          </button>
        </div>

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-[#033871] mb-6 leading-[1.05] tracking-tight" data-testid="article-title">
          {article.title}
        </h1>

        <p className="text-lg sm:text-xl text-[#5A8BA8] leading-relaxed max-w-3xl mb-8 font-light">
          {article.excerpt}
        </p>

        <div className="relative rounded-3xl overflow-hidden h-72 sm:h-[450px] shadow-2xl">
          <img
            src={getArticleImage(article)}
            alt={article.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = '/assets/padsmash_logo_main.png'; }}
          />
          {/* subtle bottom gradient for legibility */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      </header>

      {/* ARTICLE BODY — two-column layout with sticky ToC */}
      <main className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-10">
          {/* Article column */}
          <article className="min-w-0 bg-white rounded-3xl shadow-sm border border-[#E8F4FD] p-6 sm:p-10 lg:p-14" data-testid="article-content">
            <div className="article-content prose prose-slate max-w-none
              prose-headings:text-[#033871] prose-headings:font-black prose-headings:tracking-tight prose-headings:scroll-mt-20
              prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-14 prose-h2:mb-6 prose-h2:pb-3 prose-h2:relative
              prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-4
              prose-h4:text-lg prose-h4:mt-8 prose-h4:mb-3 prose-h4:text-[#0C63BF]
              prose-p:text-[#3D5870] prose-p:leading-[1.85] prose-p:mb-5 prose-p:text-[17px]
              prose-a:text-[#0C63BF] prose-a:font-semibold prose-a:no-underline prose-a:border-b-2 prose-a:border-[#0C63BF]/30 hover:prose-a:border-[#0C63BF]
              prose-strong:text-[#033871] prose-strong:font-bold
              prose-em:text-[#0C63BF]
              prose-ul:my-6 prose-ol:my-6 prose-li:mb-2.5 prose-li:text-[#3D5870] prose-li:leading-relaxed prose-li:text-[17px]
              prose-li:marker:text-[#32CD32] prose-li:marker:font-bold
              prose-table:text-sm prose-table:my-8 prose-table:rounded-xl prose-table:overflow-hidden prose-table:border prose-table:border-[#E8F4FD]
              prose-thead:bg-gradient-to-r prose-thead:from-[#033871] prose-thead:to-[#0C63BF]
              prose-th:text-white prose-th:font-bold prose-th:p-4 prose-th:text-left prose-th:text-xs prose-th:uppercase prose-th:tracking-wider
              prose-td:p-4 prose-td:border-t prose-td:border-[#E8F4FD] prose-td:text-[#3D5870]
              prose-hr:border-[#E8F4FD] prose-hr:my-12
              prose-blockquote:not-italic prose-blockquote:border-0 prose-blockquote:bg-gradient-to-br prose-blockquote:from-[#E8F4FD] prose-blockquote:to-white prose-blockquote:p-6 prose-blockquote:rounded-2xl prose-blockquote:my-8 prose-blockquote:relative prose-blockquote:shadow-sm
              prose-blockquote:before:content-['💡'] prose-blockquote:before:absolute prose-blockquote:before:-top-3 prose-blockquote:before:-left-2 prose-blockquote:before:bg-[#32CD32] prose-blockquote:before:w-9 prose-blockquote:before:h-9 prose-blockquote:before:rounded-xl prose-blockquote:before:flex prose-blockquote:before:items-center prose-blockquote:before:justify-center prose-blockquote:before:text-lg prose-blockquote:before:shadow-md
              prose-blockquote:p:text-[#033871] prose-blockquote:p:font-medium prose-blockquote:p:my-0
              prose-code:bg-[#F0F7FA] prose-code:text-[#0C63BF] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-sm prose-code:before:hidden prose-code:after:hidden
              prose-img:rounded-2xl prose-img:shadow-md prose-img:my-8">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                {article.content}
              </ReactMarkdown>
            </div>

            {/* Tags */}
            {article.keywords && article.keywords.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#E8F4FD]">
                <p className="text-xs uppercase tracking-widest text-[#5A8BA8] font-bold mb-3">{t('blog.keywords')}</p>
                <div className="flex flex-wrap gap-2">
                  {article.keywords.map(k => (
                    <span key={k} className="px-3 py-1.5 bg-gradient-to-r from-[#F0F7FA] to-[#E8F4FD] text-[#033871] text-xs rounded-full font-medium">
                      #{k.replace(/\s+/g, '-')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Author signature */}
            <div className="mt-10 pt-8 border-t border-[#E8F4FD] flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#033871] to-[#0C63BF] flex items-center justify-center text-white font-black text-lg shrink-0">
                P
              </div>
              <div>
                <p className="text-sm font-bold text-[#033871]">{t('blog.team')}</p>
                <p className="text-xs text-[#5A8BA8]">{t('blog.team_sub')}</p>
              </div>
            </div>
          </article>

          {/* Sticky ToC */}
          {toc.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-6">
                <p className="text-[10px] uppercase tracking-widest text-[#5A8BA8] font-bold mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-[#32CD32]" /> {t('blog.toc')}
                </p>
                <nav className="space-y-1" data-testid="toc">
                  {toc.map((item, i) => (
                    <a
                      key={item.id + i}
                      href={'#' + item.id}
                      className={'block text-sm leading-snug py-1.5 px-3 rounded-lg transition-all border-l-2 ' +
                        (activeId === item.id
                          ? 'text-[#033871] font-semibold bg-[#E8F4FD] border-[#0C63BF]'
                          : 'text-[#5A8BA8] hover:text-[#033871] border-transparent hover:bg-[#F0F7FA]')}
                      data-testid={'toc-link-' + item.id}
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}
        </div>

        {/* CTA */}
        <div className="mt-14 bg-gradient-to-br from-[#033871] via-[#0C63BF] to-[#0C63BF] rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#32CD32]/20 blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#32CD32]/10 blur-3xl" />
          <div className="relative">
            <Sparkles className="w-8 h-8 text-[#32CD32] mx-auto mb-3" />
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">
              {t('blog.cta2_title')}
            </h2>
            <p className="text-white/75 text-base mb-6 max-w-md mx-auto">
              {t('blog.cta2_sub')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/catalogue">
                <Button className="bg-[#32CD32] hover:bg-[#28a428] text-white font-bold h-12 px-6 gap-1.5" data-testid="cta-catalogue">
                  {t('blog.cta_listings')} <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/guide">
                <Button variant="outline" className="bg-white/10 hover:bg-white/20 border-white/30 text-white font-bold h-12 px-6" data-testid="cta-padcoach">
                  {t('blog.quiz')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Related */}
        {relatedArticles.length > 0 && (
          <section className="mt-16">
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#5A8BA8] font-bold">{t('blog.see_also')}</p>
                <h2 className="text-2xl font-black text-[#033871]">{t('blog.similar')}</h2>
              </div>
              <Link to="/blog" className="text-sm text-[#0C63BF] hover:text-[#033871] font-semibold inline-flex items-center gap-1">
                {t('blog.all_articles')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map(r => (
                <Link key={r.id} to={'/blog/' + r.slug} className="group" data-testid={'related-' + r.slug}>
                  <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full border border-[#E8F4FD] group-hover:-translate-y-1">
                    <div className="aspect-[16/10] overflow-hidden bg-[#F0F7FA]">
                      <img
                        src={getArticleImage(r)}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.currentTarget.src = '/assets/padsmash_logo_main.png'; }}
                      />
                    </div>
                    <div className="p-4">
                      <span className={'inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-2 ' + (CATEGORY_COLORS[r.category] || CATEGORY_COLORS.guide)}>
                        {t(`blog.cat_${r.category}`, 'Article')}
                      </span>
                      <h3 className="text-base font-bold text-[#033871] group-hover:text-[#0C63BF] transition-colors leading-snug line-clamp-2 mb-2">
                        {r.title}
                      </h3>
                      <span className="text-[#0C63BF] text-xs font-semibold inline-flex items-center gap-1">
                        {t('blog.read_article')} <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Heading offset for ToC anchor scroll */}
      <style>{`
        .article-content h2 { padding-left: 0.5rem; }
        @media (min-width: 640px) { .article-content h2 { padding-left: 0; } }
      `}</style>
    </div>
  );
}
